/**
 * DriverRidePanel Component
 * Shows available and active rides for drivers
 * ADDITIVE to existing package dashboard - does NOT replace package jobs
 */
import { useState, useEffect, useCallback } from "react";
import { Button } from "./ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import api from "../lib/api";
import {
  Car,
  MapPin,
  Navigation,
  DollarSign,
  Clock,
  User,
  Plane,
  Globe,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  Phone,
  Play,
  AlertCircle,
  Gift
} from "lucide-react";

const DriverRidePanel = ({ onRideUpdate }) => {
  const [availableRides, setAvailableRides] = useState([]);
  const [activeRide, setActiveRide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accepting, setAccepting] = useState(null);
  const [processingAction, setProcessingAction] = useState(null);

  useEffect(() => {
    fetchRides();
    const interval = setInterval(fetchRides, 10000); // Poll every 10s
    return () => clearInterval(interval);
  }, []);

  const fetchRides = async () => {
    try {
      const [availableRes, activeRes] = await Promise.all([
        api.get("/driver/rides/available").catch(() => ({ data: { rides: [] } })),
        api.get("/driver/rides/active").catch(() => ({ data: { ride: null } }))
      ]);
      
      setAvailableRides(availableRes.data.rides || []);
      setActiveRide(activeRes.data.ride || null);
    } catch (error) {
      console.error("Failed to fetch rides:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptRide = async (rideId) => {
    setAccepting(rideId);
    try {
      const response = await api.post(`/driver/rides/${rideId}/accept`);
      toast.success(response.data.message || "Ride accepted!");
      fetchRides();
      if (onRideUpdate) onRideUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept ride");
    } finally {
      setAccepting(null);
    }
  };

  const handleArrive = async (rideId) => {
    setProcessingAction("arrive");
    try {
      const response = await api.post(`/driver/rides/${rideId}/arrive`);
      toast.success(response.data.message || "Marked as arrived");
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to mark arrival");
    } finally {
      setProcessingAction(null);
    }
  };

  const handleVerifyPin = async (rideId, pin) => {
    setProcessingAction("verify");
    try {
      const response = await api.post(`/driver/rides/${rideId}/verify-pin`, { pin });
      toast.success(response.data.message || "PIN verified!");
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid PIN");
    } finally {
      setProcessingAction(null);
    }
  };

  const handleStartTrip = async (rideId) => {
    setProcessingAction("start");
    try {
      const response = await api.post(`/driver/rides/${rideId}/start`);
      toast.success(response.data.message || "Trip started!");
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start trip");
    } finally {
      setProcessingAction(null);
    }
  };

  const handleCompleteTrip = async (rideId) => {
    setProcessingAction("complete");
    try {
      const response = await api.post(`/driver/rides/${rideId}/complete`);
      toast.success(response.data.message || "Ride completed!");
      fetchRides();
      if (onRideUpdate) onRideUpdate();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to complete ride");
    } finally {
      setProcessingAction(null);
    }
  };

  const handleCancelRide = async (rideId) => {
    if (!window.confirm("Are you sure you want to cancel this ride?")) return;
    
    setProcessingAction("cancel");
    try {
      const response = await api.post(`/driver/rides/${rideId}/cancel`);
      toast.success(response.data.message || "Ride cancelled");
      fetchRides();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel ride");
    } finally {
      setProcessingAction(null);
    }
  };

  const openNavigation = (address, lat, lng) => {
    if (lat && lng) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`, "_blank");
    } else if (address) {
      window.open(`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}&travelmode=driving`, "_blank");
    }
  };

  const getRideTypeIcon = (type) => {
    if (type?.includes("airport")) return <Plane className="h-4 w-4 text-blue-400" />;
    if (type?.includes("tourist")) return <Globe className="h-4 w-4 text-purple-400" />;
    return <Car className="h-4 w-4 text-green-400" />;
  };

  const getRideTypeBadge = (type) => {
    if (type?.includes("airport")) return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/50">Airport</Badge>;
    if (type?.includes("tourist")) return <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/50">Tourist</Badge>;
    return <Badge className="bg-green-500/20 text-green-400 border-green-500/50">Standard</Badge>;
  };

  const formatCurrency = (amount, currency = "JMD") => {
    if (currency === "USD") return `$${amount?.toFixed(2) || 0}`;
    return `J$${amount?.toLocaleString() || 0}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Active Ride Card */}
      {activeRide && (
        <Card className="bg-gradient-to-r from-amber-950/50 to-slate-800 border-amber-500" data-testid="active-ride-card">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Car className="h-5 w-5 text-amber-500" />
                Active Ride
              </CardTitle>
              <Badge className="bg-amber-500 text-black">{activeRide.status?.replace(/_/g, " ").toUpperCase()}</Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Ride Info */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-green-400 mt-1" />
                  <div>
                    <p className="text-xs text-slate-400">Pickup</p>
                    <p className="text-sm text-white">{activeRide.pickup?.address || "Address pending"}</p>
                    {activeRide.location_intel?.pickup_coords && (
                      <p className="text-xs text-slate-500 font-mono">{activeRide.location_intel.pickup_coords.lat?.toFixed(5)}, {activeRide.location_intel.pickup_coords.lng?.toFixed(5)}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-red-400 mt-1" />
                  <div>
                    <p className="text-xs text-slate-400">Dropoff</p>
                    <p className="text-sm text-white">{activeRide.dropoff?.address || "Address pending"}</p>
                    {activeRide.location_intel?.dropoff_coords && (
                      <p className="text-xs text-slate-500 font-mono">{activeRide.location_intel.dropoff_coords.lat?.toFixed(5)}, {activeRide.location_intel.dropoff_coords.lng?.toFixed(5)}</p>
                    )}
                    {activeRide.location_intel?.dropoff_confidence && activeRide.location_intel.dropoff_confidence !== 'suggested' && (
                      <Badge className="mt-1 bg-emerald-500/20 text-emerald-400 border-emerald-500/50 text-xs">
                        {activeRide.location_intel.dropoff_confidence === 'trusted' ? 'Trusted' : 'Verified'}
                      </Badge>
                    )}
                    {activeRide.location_intel?.dropoff_near && (
                      <p className="text-xs text-amber-400 mt-0.5">Near {activeRide.location_intel.dropoff_near} ({activeRide.location_intel.dropoff_near_distance_m < 1000 ? `${activeRide.location_intel.dropoff_near_distance_m}m` : `${(activeRide.location_intel.dropoff_near_distance_m / 1000).toFixed(1)}km`})</p>
                    )}
                  </div>
                </div>
                {activeRide.location_intel?.driver_notes && (
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-400 mt-1" />
                    <div>
                      <p className="text-xs text-slate-400">Driver Notes</p>
                      <p className="text-sm text-amber-300">{activeRide.location_intel.driver_notes}</p>
                    </div>
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <User className="h-4 w-4 text-slate-400" />
                  <span className="text-sm text-white">{activeRide.passenger?.name || "Passenger"}</span>
                </div>
                {activeRide.passenger?.phone && (
                  <a href={`tel:${activeRide.passenger.phone}`} className="flex items-center gap-2 text-blue-400 hover:text-blue-300">
                    <Phone className="h-4 w-4" />
                    <span className="text-sm">{activeRide.passenger.phone}</span>
                  </a>
                )}
                <div className="flex items-center gap-2">
                  <DollarSign className="h-4 w-4 text-green-400" />
                  <span className="text-lg font-bold text-green-400">
                    {formatCurrency(activeRide.driver_payout || activeRide.fare, activeRide.currency)}
                  </span>
                </div>
                {activeRide.early_network_boost && (
                  <div className="flex items-center gap-1 text-amber-400">
                    <Gift className="h-3 w-3" />
                    <span className="text-xs">+{formatCurrency(activeRide.early_network_boost)} boost</span>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons based on status */}
            <div className="flex gap-2 flex-wrap">
              {activeRide.status === "accepted" && (
                <>
                  <Button 
                    onClick={() => openNavigation(activeRide.pickup?.address, activeRide.pickup?.lat, activeRide.pickup?.lng)}
                    className="bg-blue-600 hover:bg-blue-500 flex-1"
                    data-testid="navigate-to-pickup-btn"
                  >
                    <Navigation className="h-4 w-4 mr-2" />Navigate to Pickup
                  </Button>
                  <Button 
                    onClick={() => handleArrive(activeRide.id)}
                    disabled={processingAction === "arrive"}
                    className="bg-amber-600 hover:bg-amber-500"
                    data-testid="arrive-btn"
                  >
                    {processingAction === "arrive" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                    Arrived
                  </Button>
                </>
              )}
              
              {activeRide.status === "arrived" && (
                <div className="w-full space-y-2">
                  <p className="text-amber-400 text-sm">Ask passenger for their 4-digit PIN</p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      maxLength={4}
                      placeholder="Enter PIN"
                      id="pin-input"
                      className="flex-1 bg-slate-800 border border-slate-700 rounded px-3 py-2 text-white text-center text-lg tracking-widest"
                      data-testid="pin-input"
                    />
                    <Button 
                      onClick={() => handleVerifyPin(activeRide.id, document.getElementById("pin-input").value)}
                      disabled={processingAction === "verify"}
                      className="bg-green-600 hover:bg-green-500"
                      data-testid="verify-pin-btn"
                    >
                      {processingAction === "verify" ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}
                    </Button>
                  </div>
                </div>
              )}
              
              {activeRide.status === "verified" && (
                <Button 
                  onClick={() => handleStartTrip(activeRide.id)}
                  disabled={processingAction === "start"}
                  className="bg-green-600 hover:bg-green-500 flex-1"
                  data-testid="start-trip-btn"
                >
                  {processingAction === "start" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
                  Start Trip
                </Button>
              )}
              
              {activeRide.status === "in_progress" && (
                <>
                  <Button 
                    onClick={() => openNavigation(activeRide.dropoff?.address, activeRide.dropoff?.lat, activeRide.dropoff?.lng)}
                    className="bg-blue-600 hover:bg-blue-500 flex-1"
                    data-testid="navigate-to-dropoff-btn"
                  >
                    <Navigation className="h-4 w-4 mr-2" />Navigate to Dropoff
                  </Button>
                  <Button 
                    onClick={() => handleCompleteTrip(activeRide.id)}
                    disabled={processingAction === "complete"}
                    className="bg-green-600 hover:bg-green-500"
                    data-testid="complete-trip-btn"
                  >
                    {processingAction === "complete" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                    Complete
                  </Button>
                </>
              )}
              
              {["accepted", "arrived"].includes(activeRide.status) && (
                <Button 
                  variant="outline"
                  onClick={() => handleCancelRide(activeRide.id)}
                  disabled={processingAction === "cancel"}
                  className="border-red-500 text-red-500 hover:bg-red-500/10"
                  data-testid="cancel-ride-btn"
                >
                  <XCircle className="h-4 w-4 mr-2" />Cancel
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Available Rides */}
      <Card className="bg-slate-900 border-slate-800" data-testid="available-rides-card">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg text-white flex items-center gap-2">
              <Car className="h-5 w-5 text-green-500" />
              Available Rides ({availableRides.length})
            </CardTitle>
            <Button size="sm" variant="ghost" onClick={fetchRides} className="text-slate-400 hover:text-white">
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {availableRides.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Car className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No ride requests available</p>
              <p className="text-xs mt-1">Stay online - new rides appear automatically</p>
            </div>
          ) : (
            <div className="space-y-3">
              {availableRides.map((ride) => (
                <div 
                  key={ride.id} 
                  className="rounded-lg border border-slate-700 bg-slate-800/50 overflow-hidden"
                  data-testid={`ride-offer-${ride.id}`}
                >
                  {/* Header */}
                  <div className="px-4 py-2 bg-slate-700/50 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getRideTypeIcon(ride.ride_type)}
                      {getRideTypeBadge(ride.ride_type)}
                      {ride.is_scheduled && (
                        <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/50">
                          <Clock className="h-3 w-3 mr-1" />Scheduled
                        </Badge>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-400">
                        {formatCurrency(ride.driver_payout || ride.estimated_fare, ride.currency)}
                      </p>
                      {ride.early_network_boost > 0 && (
                        <p className="text-xs text-amber-400 flex items-center gap-1 justify-end">
                          <Gift className="h-3 w-3" />
                          +{formatCurrency(ride.early_network_boost)} boost
                        </p>
                      )}
                    </div>
                  </div>
                  
                  {/* Body */}
                  <div className="p-4 space-y-3">
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-green-400 mt-1 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-400">Pickup</p>
                        <p className="text-sm text-white truncate">{ride.pickup?.address || "Address pending"}</p>
                        {ride.location_intel?.pickup_coords && (
                          <p className="text-xs text-slate-500 font-mono">{ride.location_intel.pickup_coords.lat?.toFixed(5)}, {ride.location_intel.pickup_coords.lng?.toFixed(5)}</p>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-2">
                      <MapPin className="h-4 w-4 text-red-400 mt-1 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-slate-400">Dropoff</p>
                        <p className="text-sm text-white truncate">{ride.dropoff?.address || "Address pending"}</p>
                        {ride.location_intel?.dropoff_coords && (
                          <p className="text-xs text-slate-500 font-mono">{ride.location_intel.dropoff_coords.lat?.toFixed(5)}, {ride.location_intel.dropoff_coords.lng?.toFixed(5)}</p>
                        )}
                        {ride.location_intel?.dropoff_confidence && ride.location_intel.dropoff_confidence !== 'suggested' && (
                          <Badge className="mt-0.5 bg-emerald-500/20 text-emerald-400 border-emerald-500/50 text-xs">
                            {ride.location_intel.dropoff_confidence === 'trusted' ? 'Trusted' : 'Verified'}
                          </Badge>
                        )}
                        {ride.location_intel?.dropoff_near && (
                          <p className="text-xs text-amber-400 mt-0.5">Near {ride.location_intel.dropoff_near}</p>
                        )}
                      </div>
                    </div>
                    
                    {ride.location_intel?.driver_notes && (
                      <div className="flex items-start gap-2">
                        <AlertCircle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
                        <p className="text-xs text-amber-300 flex-1">{ride.location_intel.driver_notes}</p>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-4 text-xs text-slate-400">
                      <span>{ride.distance_km?.toFixed(1) || "--"} km</span>
                      <span>{ride.duration_min?.toFixed(0) || "--"} min</span>
                      {ride.vehicle_requirement && (
                        <Badge variant="outline" className="text-xs">{ride.vehicle_requirement}</Badge>
                      )}
                    </div>
                    
                    <Button 
                      onClick={() => handleAcceptRide(ride.id)}
                      disabled={accepting === ride.id || activeRide}
                      className="w-full bg-green-600 hover:bg-green-500 disabled:opacity-50"
                      data-testid={`accept-ride-${ride.id}`}
                    >
                      {accepting === ride.id ? (
                        <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      ) : (
                        <CheckCircle className="h-4 w-4 mr-2" />
                      )}
                      {activeRide ? "Complete active ride first" : "Accept Ride"}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DriverRidePanel;
